// Validation shared by the client UI, the admin panel and the server (the server is the authority).
import type { BannerPlacement, PlacementBounds, PlacementKind, StorefrontSlot } from './types';

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f​-‏‪-‮⁦-⁩]/g;

/** Display names: 2–24 visible characters; letters (any script), digits, spaces and . _ - ' only. */
export function validateDisplayName(input: unknown): Result<string> {
  if (typeof input !== 'string') return { ok: false, error: 'Enter a display name.' };
  const value = input.replace(CONTROL_CHARS, '').replace(/\s+/g, ' ').trim();
  if (value.length < 2) return { ok: false, error: 'Display name must be at least 2 characters.' };
  if (value.length > 24) return { ok: false, error: 'Display name must be 24 characters or fewer.' };
  if (!/^[\p{L}\p{M}\p{N} ._'\-]+$/u.test(value)) {
    return { ok: false, error: "Use letters, numbers, spaces and . _ - ' only." };
  }
  if (/https?:|www\.|\.com\b/i.test(value)) return { ok: false, error: 'Links are not allowed in display names.' };
  return { ok: true, value };
}

/**
 * Instagram handles: optional. Accepts "@handle", "handle" or an instagram.com profile URL.
 * Instagram usernames are 1–30 characters of letters, digits, periods and underscores; they
 * cannot start or end with a period or contain consecutive periods.
 * Returns null for an empty input (handle not provided).
 */
export function normalizeInstagramHandle(input: unknown): Result<string | null> {
  if (input === null || input === undefined) return { ok: true, value: null };
  if (typeof input !== 'string') return { ok: false, error: 'Invalid Instagram handle.' };
  let v = input.replace(CONTROL_CHARS, '').trim();
  if (v === '') return { ok: true, value: null };
  const url = v.match(/^(?:https?:\/\/)?(?:www\.)?instagram\.com\/([^/?#]+)\/?(?:[?#].*)?$/i);
  if (url) v = url[1];
  v = v.replace(/^@+/, '').toLowerCase();
  if (v.length < 1 || v.length > 30) return { ok: false, error: 'Instagram handles are 1–30 characters.' };
  if (!/^[a-z0-9._]+$/.test(v)) return { ok: false, error: 'Instagram handles use only letters, numbers, periods and underscores.' };
  if (v.startsWith('.') || v.endsWith('.')) return { ok: false, error: 'Instagram handles cannot start or end with a period.' };
  if (v.includes('..')) return { ok: false, error: 'Instagram handles cannot contain consecutive periods.' };
  return { ok: true, value: v };
}

export function instagramProfileUrl(handle: string): string {
  return `https://www.instagram.com/${encodeURIComponent(handle)}/`;
}

export function validateShopText(field: 'name' | 'tagline' | 'description' | 'category', input: unknown): Result<string> {
  const limits = { name: [2, 48], tagline: [0, 80], description: [0, 600], category: [2, 32] } as const;
  const [min, max] = limits[field];
  if (typeof input !== 'string') return min === 0 ? { ok: true, value: '' } : { ok: false, error: `${field} is required.` };
  const value = input.replace(CONTROL_CHARS, '').replace(/[ \t]+/g, ' ').trim();
  if (value.length < min) return { ok: false, error: `${field} must be at least ${min} characters.` };
  if (value.length > max) return { ok: false, error: `${field} must be ${max} characters or fewer.` };
  return { ok: true, value };
}

export function validateWebsite(input: unknown): Result<string | null> {
  if (input === null || input === undefined || input === '') return { ok: true, value: null };
  if (typeof input !== 'string' || input.length > 200) return { ok: false, error: 'Website must be a valid https:// URL.' };
  try {
    const u = new URL(input.trim());
    if (u.protocol !== 'https:') return { ok: false, error: 'Website must use https://.' };
    if (u.username || u.password) return { ok: false, error: 'Website must not contain credentials.' };
    return { ok: true, value: u.toString() };
  } catch {
    return { ok: false, error: 'Website must be a valid https:// URL.' };
  }
}

// ───────────────────────── Storefront placement bounds ─────────────────────────

export const PLACEMENT_KINDS: PlacementKind[] = ['fascia', 'shopfront', 'upper-banner'];

export const PLACEMENT_LABELS: Record<PlacementKind, string> = {
  fascia: 'Fascia signboard (above the arcade)',
  shopfront: 'Shopfront window poster (inside the arcade)',
  'upper-banner': 'Upper-facade vertical banner',
};

/** Physical bounds for each placement on a slot's facade. All values in metres. */
export function computePlacementBounds(width: number, height: number, arcade: { depth: number; height: number } | null): Record<PlacementKind, PlacementBounds> {
  const margin = Math.min(0.25, width * 0.05);
  const arcadeH = arcade ? arcade.height : 3.2;
  const fasciaBottom = arcadeH + 0.1;
  const fasciaTop = Math.min(height - 0.4, arcadeH + 1.8);
  const upperBottom = Math.min(height - 1.5, fasciaTop + 0.4);
  const upperTop = height - 0.4;
  return {
    fascia: {
      uMin: margin, uMax: width - margin, vMin: fasciaBottom, vMax: Math.max(fasciaBottom + 0.5, fasciaTop),
      minW: 0.6, maxW: Math.max(0.6, width - 2 * margin), minH: 0.3, maxH: Math.max(0.3, Math.min(1.6, fasciaTop - fasciaBottom)),
      depth: 0.04,
    },
    shopfront: {
      uMin: margin + 0.2, uMax: width - margin - 0.2, vMin: 0.4, vMax: Math.max(1.0, arcadeH - 0.35),
      minW: 0.4, maxW: Math.max(0.4, width - 2 * margin - 0.4), minH: 0.4, maxH: Math.max(0.4, arcadeH - 0.75),
      depth: arcade ? -arcade.depth + 0.06 : 0.04,
    },
    'upper-banner': {
      uMin: margin, uMax: width - margin, vMin: upperBottom, vMax: Math.max(upperBottom + 1, upperTop),
      minW: 0.5, maxW: Math.max(0.5, Math.min(3.5, width - 2 * margin)), minH: 1.0, maxH: Math.max(1.0, upperTop - upperBottom),
      depth: 0.06,
    },
  };
}

/** Clamp a placement so its rectangle lies fully inside the slot's bounds for its kind. */
export function clampPlacement(slot: Pick<StorefrontSlot, 'bounds'>, p: BannerPlacement): BannerPlacement {
  const b = slot.bounds[p.kind];
  const width = clamp(finite(p.width, b.minW), b.minW, Math.min(b.maxW, b.uMax - b.uMin));
  const height = clamp(finite(p.height, b.minH), b.minH, Math.min(b.maxH, b.vMax - b.vMin));
  const u = clamp(finite(p.u, (b.uMin + b.uMax) / 2), b.uMin + width / 2, b.uMax - width / 2);
  const v = clamp(finite(p.v, (b.vMin + b.vMax) / 2), b.vMin + height / 2, b.vMax - height / 2);
  return { ...p, u: round3(u), v: round3(v), width: round3(width), height: round3(height) };
}

export function placementWithinBounds(slot: Pick<StorefrontSlot, 'bounds'>, p: BannerPlacement): boolean {
  const c = clampPlacement(slot, p);
  const eps = 1e-3;
  return Math.abs(c.u - p.u) < eps && Math.abs(c.v - p.v) < eps && Math.abs(c.width - p.width) < eps && Math.abs(c.height - p.height) < eps;
}

export function defaultPlacement(slot: Pick<StorefrontSlot, 'bounds'>, kind: PlacementKind, assetId: string, aspect = 4): BannerPlacement {
  const b = slot.bounds[kind];
  let width = Math.min(b.maxW, b.uMax - b.uMin) * (kind === 'fascia' ? 0.9 : 0.7);
  let height = width / aspect;
  if (height > b.maxH) {
    height = b.maxH;
    width = height * aspect;
  }
  return clampPlacement(slot, {
    id: '',
    kind,
    assetId,
    u: (b.uMin + b.uMax) / 2,
    v: (b.vMin + b.vMax) / 2,
    width,
    height: Math.max(height, b.minH),
    fit: 'cover',
    illuminated: kind !== 'shopfront',
  });
}

function clamp(x: number, lo: number, hi: number) {
  if (hi < lo) return (lo + hi) / 2;
  return Math.min(hi, Math.max(lo, x));
}
function finite(x: unknown, fallback: number) {
  return typeof x === 'number' && Number.isFinite(x) ? x : fallback;
}
function round3(x: number) {
  return Math.round(x * 1000) / 1000;
}
